/**
 * AtlasIntroVideoModule — the INTRO between the traveler's BEGIN and the
 * world's load (2026-09-07; 2026-09-09: the narration card, and the world
 * WAITS).
 *
 * The traveler's BEGIN emits `atlas:journey-begin` (lib/journey.ts). This
 * module is the JOURNEY GATE: it runs the intro and only then forwards
 * `atlas:menu-enter` — the event the world's entry (AtlasNetworkModule), the
 * load screen's clock (AtlasLoadingModule) and the menu's dismissal hang
 * off — so nothing of the world loads until the film ends or is skipped.
 * When the decision is NOT to play (opt-out, automation, resume, once) the
 * forward is immediate: every probe that clicks BEGIN enters as before.
 *
 * Two stages in ONE overlay (`#intro-video`, z 90, black):
 *   1. THE CARD — intro/intro-img.png fades in from black (1.5 s, once the
 *      7 MB PNG has DECODED — fading a half-loaded picture is not a fade);
 *      at the end of the fade the narration (intro/intro-audio.mp3) and the
 *      music (intro/intro_music.mp3, at half gain — "slightly quieter than
 *      the narration") start TOGETHER and the picture — held CLOSE (scale
 *      1.4) through the fade — begins a slow linear zoom-out timed to the
 *      music's length, so it fills the whole screen exactly as the music
 *      ends and the film starts at once (reduced motion: the full picture,
 *      still). A click skips the card straight
 *      to the film (both tracks ramp out in 120 ms) — returning players
 *      need not listen again. The film's <video> is created during the card
 *      (hidden, preload) so it starts the instant the card ends; the picture
 *      stays beneath it as its poster until the first frame paints.
 *   2. THE FILM — videos/intro.mp4 as before: a click skips it; ended /
 *      error / autoplay refused even muted / the 120 s failsafe end it.
 *      No keyboard skip — the click is the one way out of either stage.
 * On the film's exit the overlay fades (350 ms), the sources are cleared
 * (the decoder's 137 MB) and atlas:menu-enter is forwarded with the
 * journey's worldId — exactly once per journey.
 * Autoplay: the click that began the journey is a user activation, so the
 * tracks and the first play() of the film are with sound; a refused film
 * retries MUTED and only then gives up; refused tracks yield to the film.
 * The game is MUTED for the whole intro (lib/audio_mute: every audio
 * owner's mute stage) and unmuted the instant the film is skipped or ends.
 * Never on a reconnect RESUME (no menu was shown), never under automation
 * unless localStorage atlas:intro-video = 'on', never when 'off' / ?intro=off,
 * once per page load. Decisions live in lib/intro_video.ts (pure, harnessed).
 */
import type { App, IModule } from 'enginex';
import { bus } from '../bus.js';
import { pendingResume } from '../lib/session_resume.js';
import { setGameAudioMuted, isGameAudioMuted, audioMuteOwners } from '../lib/audio_mute.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import {
  INTRO_VIDEO_SRC, INTRO_IMAGE_SRC, INTRO_NARRATION_SRC, INTRO_MUSIC_SRC,
  INTRO_NARRATION_GAIN, INTRO_MUSIC_GAIN, INTRO_CARD_FADE_MS, INTRO_DECODE_MAX_MS, INTRO_AUDIO_RAMP_MS,
  INTRO_FADE_MS, INTRO_GESTURE_MS, INTRO_PREF_KEY, introDecision, introExit, cardExit, cardZoomPlan, stageHint,
  type IntroEndReason, type IntroSkipReason, type IntroStage, type IntroHandoff,
} from '../lib/intro_video.js';

/** Timing overrides (the jsdom harness runs the card's fade in tens of ms). */
export interface IntroTiming { cardFadeMs?: number; fadeMs?: number; decodeMaxMs?: number; rampMs?: number }

/** Resolve once the picture has decoded — or at once where decode() is not a
 *  thing (jsdom), or after the cap; a broken file resolves too (the card then
 *  plays over black — the narration still matters). */
function decoded(img: HTMLImageElement, maxMs: number): Promise<void> {
  const dec = (img as { decode?: () => Promise<void> }).decode;
  if (typeof dec !== 'function') return Promise.resolve();
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = (): void => { if (!done) { done = true; resolve(); } };
    window.setTimeout(finish, maxMs);
    try { dec.call(img).then(finish, finish); } catch { finish(); }
  });
}

export class AtlasIntroVideoModule implements IModule {
  readonly name = 'atlas-intro-video';
  private overlay: HTMLDivElement | null = null;
  private img: HTMLImageElement | null = null;
  private narration: HTMLAudioElement | null = null;
  private music: HTMLAudioElement | null = null;
  private video: HTMLVideoElement | null = null;
  private hint: HTMLDivElement | null = null;
  private stage: IntroStage | null = null;
  private played = false;
  private resumed = false;
  private startedAt = 0;
  private poll: number | null = null;
  private fadeTimer: number | null = null;
  private rampTimers: number[] = [];
  private readonly offs: Array<() => void> = [];
  private lastDecision: 'play' | IntroSkipReason | null = null;
  private cardEndedWith: IntroEndReason | null = null;
  private endedWith: IntroEndReason | null = null;
  private muted = false;
  private ending = false;
  private worldId: bigint | null = null;
  private forwarded = false;
  private handoff: IntroHandoff | null = null;
  private lastPointerDownAt = -1e9;
  private reducedMotion = false;
  private zoomMs: number | null = null;
  private card = { musicEnded: false, narrationEnded: false, musicError: false, narrationError: false };
  private readonly cardFadeMs: number;
  private readonly fadeMs: number;
  private readonly decodeMaxMs: number;
  private readonly rampMs: number;

  constructor(timing: IntroTiming = {}) {
    this.cardFadeMs = timing.cardFadeMs ?? INTRO_CARD_FADE_MS;
    this.fadeMs = timing.fadeMs ?? INTRO_FADE_MS;
    this.decodeMaxMs = timing.decodeMaxMs ?? INTRO_DECODE_MAX_MS;
    this.rampMs = timing.rampMs ?? INTRO_AUDIO_RAMP_MS;
  }

  init(_app: App): void {
    // A reconnect reload carries a fresh resume record at init (the network
    // module consumes it later, on account-ready) — no menu, no intro.
    try { this.resumed = !!pendingResume(); } catch { this.resumed = false; }
    this.offs.push(bus.on('atlas:journey-begin', ({ worldId }) => this.onJourney(worldId)));
    (globalThis as Record<string, unknown>).__introVideo = () => ({
      shown: !!this.overlay, stage: this.stage, decision: this.lastDecision,
      cardEndedWith: this.cardEndedWith, endedWith: this.endedWith, muted: this.muted,
      forwarded: this.forwarded, handoff: this.handoff, worldId: this.worldId === null ? null : String(this.worldId),
      gameMuted: isGameAudioMuted(), muteOwners: audioMuteOwners(),
      card: this.overlay ? {
        imgOpacity: this.img?.style.opacity ?? null, imgSrc: this.img?.getAttribute('src') ?? null,
        narrationTime: this.narration ? +this.narration.currentTime.toFixed(2) : null, musicTime: this.music ? +this.music.currentTime.toFixed(2) : null,
        narrationVolume: this.narration?.volume ?? null, musicVolume: this.music?.volume ?? null,
        narrationPaused: this.narration?.paused ?? null, musicPaused: this.music?.paused ?? null,
        narrationReady: this.narration?.readyState ?? null, musicReady: this.music?.readyState ?? null,
        imgTransform: this.img?.style.transform ?? null, zoomMs: this.zoomMs, musicDuration: this.music && Number.isFinite(this.music.duration) ? +this.music.duration.toFixed(2) : null,
      } : null,
      currentTime: this.video ? +this.video.currentTime.toFixed(2) : null, paused: this.video ? this.video.paused : null,
      readyState: this.video ? this.video.readyState : null, elapsedS: this.overlay ? +((performance.now() - this.startedAt) / 1000).toFixed(1) : null,
    });
  }

  /** The journey gate: play the intro then forward, or forward at once. */
  private onJourney(worldId: bigint | null): void {
    if (this.overlay) return; // a journey is already under way — it forwards when it ends
    let pref: string | null = null;
    try { pref = window.localStorage.getItem(INTRO_PREF_KEY); } catch { /* storage blocked */ }
    let query: string | null = null;
    try { query = new URLSearchParams(window.location.search).get('intro'); } catch { /* no location */ }
    const d = introDecision({
      hasLoadScreen: !!document.getElementById('load-screen'),
      pref, query,
      webdriver: !!(navigator as { webdriver?: boolean }).webdriver,
      resumed: this.resumed,
      played: this.played,
    });
    this.worldId = worldId;
    this.forwarded = false;
    this.handoff = null;
    if (!d.play) { this.lastDecision = d.why; this.forward('immediate'); return; }
    this.lastDecision = 'play';
    this.played = true;
    this.show();
  }

  /** atlas:menu-enter — the world's load begins HERE, exactly once per journey. */
  private forward(how: IntroHandoff): void {
    if (this.forwarded) return;
    this.forwarded = true;
    this.handoff = how;
    bus.emit('atlas:menu-enter', { worldId: this.worldId });
  }

  private show(): void {
    const overlay = document.createElement('div');
    overlay.id = 'intro-video';
    // (individual assignments: jsdom's cssText parser drops z-index and object-fit)
    const os = overlay.style;
    os.position = 'fixed'; os.inset = '0'; os.zIndex = '90'; os.background = '#000'; os.cursor = 'pointer'; os.opacity = '1';
    os.transition = `opacity ${this.fadeMs}ms ease`; os.overflow = 'hidden';

    // ── The card: the picture (fades in), the two tracks ──
    const img = document.createElement('img');
    img.id = 'intro-card';
    img.alt = '';
    img.decoding = 'async';
    img.draggable = false;
    const is = img.style;
    is.position = 'absolute'; is.inset = '0'; is.width = '100%'; is.height = '100%'; is.objectFit = 'cover'; is.opacity = '0';
    is.transition = `opacity ${this.cardFadeMs}ms ease`; is.pointerEvents = 'none';
    // Close through the fade; the zoom-out starts with the tracks (startTracks).
    try { this.reducedMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { this.reducedMotion = false; }
    is.transformOrigin = '50% 50%'; is.willChange = 'opacity, transform';
    is.transform = `scale(${cardZoomPlan(null, this.reducedMotion).from})`;
    img.src = INTRO_IMAGE_SRC;
    overlay.appendChild(img);
    const track = (id: string, src: string, gain: number): HTMLAudioElement => {
      const a = document.createElement('audio');
      a.id = id;
      a.preload = 'auto';
      a.src = src;
      // THE MASTER COVERS THE INTRO TOO (2026-09-20). These are plain media elements, outside Web Audio — and a master
      // volume that spared the loudest minute of the session would not be a master. They take master × Cinematics from
      // the shared runtime (1 × 1 by default, so the card's 1 / 0.5 balance is unchanged). Kept as elements on purpose:
      // routing them through the AudioContext would make the narration depend on the context being unlocked.
      try { a.volume = Math.min(1, gain * audioRuntime().elementGain('cinematic')); } catch { /* jsdom guards the range */ }
      overlay.appendChild(a);
      return a;
    };
    const narration = track('intro-narration', INTRO_NARRATION_SRC, INTRO_NARRATION_GAIN);
    const music = track('intro-music', INTRO_MUSIC_SRC, INTRO_MUSIC_GAIN);
    narration.addEventListener('ended', () => { this.card.narrationEnded = true; this.tick(); });
    narration.addEventListener('error', () => { this.card.narrationError = true; this.tick(); });
    music.addEventListener('ended', () => { this.card.musicEnded = true; this.tick(); });
    music.addEventListener('error', () => { this.card.musicError = true; this.tick(); });

    // ── The film: created now (preload) and shown when the card ends ──
    const video = document.createElement('video');
    video.src = INTRO_VIDEO_SRC;
    video.playsInline = true;
    video.preload = 'auto';
    video.disablePictureInPicture = true;
    try { video.volume = Math.min(1, audioRuntime().elementGain('cinematic')); } catch { /* jsdom guards the range */ }
    video.setAttribute('playsinline', '');
    const vs = video.style;
    vs.position = 'absolute'; vs.inset = '0'; vs.width = '100%'; vs.height = '100%'; vs.objectFit = 'cover'; vs.background = 'transparent'; vs.pointerEvents = 'none';
    vs.display = 'none';
    overlay.appendChild(video);
    video.addEventListener('ended', () => this.end('ended'));
    video.addEventListener('error', () => this.end('error'));

    const hint = document.createElement('div');
    hint.id = 'intro-video-hint';
    hint.textContent = stageHint('card');
    hint.style.cssText = 'position:absolute;right:22px;bottom:18px;font:11px ui-monospace,monospace;letter-spacing:3px;text-transform:uppercase;'
      + 'color:rgba(220,228,240,0.55);text-shadow:0 1px 3px rgba(0,0,0,0.8);pointer-events:none;opacity:0;transition:opacity 900ms ease 1200ms;';
    overlay.appendChild(hint);
    // ONE gesture, one skip: a real click fires pointerdown THEN click, and with
    // two stages the first skipped the card and the second skipped the film
    // (the browser probe, 2026-09-09). pointerdown acts; the click that follows
    // it within the gesture window is the same press. A lone click (a scripted
    // .click(), a keyboard-activated element) still acts.
    const onGesture = (e: Event): void => {
      e.preventDefault();
      const now = performance.now();
      if (e.type === 'click' && now - this.lastPointerDownAt < INTRO_GESTURE_MS) return;
      if (e.type === 'pointerdown') this.lastPointerDownAt = now;
      this.click();
    };
    overlay.addEventListener('pointerdown', onGesture);
    overlay.addEventListener('click', onGesture);
    document.body.appendChild(overlay);
    this.overlay = overlay; this.img = img; this.narration = narration; this.music = music; this.video = video; this.hint = hint;
    this.card = { musicEnded: false, narrationEnded: false, musicError: false, narrationError: false };
    setGameAudioMuted(true); // the intro has the room: no sfx, music, ambience or zones bleed in
    this.stage = 'card';
    this.startedAt = performance.now();
    this.cardEndedWith = null;
    this.endedWith = null;
    this.ending = false;
    this.muted = false;
    this.zoomMs = null;
    window.requestAnimationFrame(() => { hint.style.opacity = '1'; });
    // The fade-in waits for the decode; the tracks wait for the fade.
    void decoded(img, this.decodeMaxMs).then(() => {
      if (this.stage !== 'card' || this.overlay !== overlay) return;
      img.style.opacity = '1';
      this.fadeTimer = window.setTimeout(() => { this.fadeTimer = null; this.startTracks(); }, this.cardFadeMs);
    });
    // The exits (belt and braces beside the media events) + the failsafes, on a plain clock.
    this.poll = window.setInterval(() => this.tick(), 200);
  }

  /** Both tracks TOGETHER at the end of the fade; refused → the film. */
  private startTracks(): void {
    if (this.stage !== 'card' || this.ending) return;
    const play = (el: HTMLMediaElement): Promise<void> => {
      const p = el.play();
      return p && typeof (p as Promise<void>).then === 'function' ? (p as Promise<void>) : Promise.resolve();
    };
    const n = this.narration, m = this.music;
    if (!n || !m) return;
    // The zoom-out rides the music's clock: its metadata when loaded, else the shipped length.
    const plan = cardZoomPlan(m.duration, this.reducedMotion);
    this.zoomMs = plan.ms;
    if (this.img) {
      const is = this.img.style;
      is.transition = `opacity ${this.cardFadeMs}ms ease, transform ${plan.ms}ms linear`;
      is.transform = `scale(${plan.to})`;
    }
    Promise.all([play(n), play(m)]).catch(() => this.endCard('blocked'));
  }

  private tick(): void {
    if (!this.overlay || this.ending) return;
    const elapsedS = (performance.now() - this.startedAt) / 1000;
    if (this.stage === 'card') {
      const reason = cardExit({
        musicEnded: this.card.musicEnded || !!this.music?.ended,
        narrationEnded: this.card.narrationEnded || !!this.narration?.ended,
        musicError: this.card.musicError, narrationError: this.card.narrationError,
        clicked: false, elapsedS,
      });
      if (reason) this.endCard(reason);
    } else if (this.stage === 'video') {
      const reason = introExit({ ended: !!this.video && this.video.ended, clicked: false, error: false, elapsedS });
      if (reason) this.end(reason);
    }
  }

  private click(): void {
    if (this.stage === 'card') this.endCard('skipped');
    else this.end('skipped');
  }

  /** The card's exit — every reason leads to the film, at once. */
  private endCard(reason: IntroEndReason): void {
    if (this.stage !== 'card' || this.ending) return;
    this.cardEndedWith = reason;
    if (this.fadeTimer !== null) { window.clearTimeout(this.fadeTimer); this.fadeTimer = null; }
    this.rampOut([this.narration, this.music], reason === 'skipped' ? this.rampMs : 0);
    this.startFilm();
  }

  /** Ramp the tracks to silence over `ms` (a skip mid-word must not pop), then stop them. */
  private rampOut(els: Array<HTMLMediaElement | null>, ms: number): void {
    const live = els.filter((e): e is HTMLMediaElement => !!e);
    const stop = (): void => { for (const e of live) { try { e.pause(); } catch { /* fine */ } } };
    if (ms <= 0) { stop(); return; }
    const steps = 6, dt = ms / steps;
    const v0 = live.map((e) => e.volume);
    for (let i = 1; i <= steps; i++) {
      this.rampTimers.push(window.setTimeout(() => {
        live.forEach((e, k) => { try { e.volume = v0[k] * (1 - i / steps); } catch { /* fine */ } });
        if (i === steps) stop();
      }, dt * i));
    }
  }

  private startFilm(): void {
    const video = this.video, hint = this.hint;
    if (!video) return;
    this.stage = 'video';
    this.startedAt = performance.now();
    if (hint) hint.textContent = stageHint('video');
    video.style.display = '';
    // Sound first (the journey began with a click); muted if the browser refuses; else the loading page.
    const attempt = (): Promise<void> => { const p = video.play(); return p && typeof (p as Promise<void>).then === 'function' ? (p as Promise<void>) : Promise.resolve(); };
    attempt().catch(() => {
      this.muted = true;
      video.muted = true;
      return attempt();
    }).catch(() => this.end('blocked'));
  }

  /** The film's exit: the overlay fades, the world's load begins. */
  private end(reason: IntroEndReason): void {
    const overlay = this.overlay;
    if (!overlay || this.ending) return;
    this.ending = true;
    this.endedWith = reason;
    setGameAudioMuted(false); // the game's sound returns the instant the film is skipped or ends
    if (this.poll !== null) { window.clearInterval(this.poll); this.poll = null; }
    if (this.fadeTimer !== null) { window.clearTimeout(this.fadeTimer); this.fadeTimer = null; }
    for (const t of this.rampTimers.splice(0)) window.clearTimeout(t);
    const media: Array<HTMLMediaElement | null> = [this.video, this.narration, this.music];
    overlay.style.pointerEvents = 'none';
    overlay.style.opacity = '0';
    this.forward('after-intro'); // the load screen comes up beneath the fade
    window.setTimeout(() => {
      for (const el of media) {
        if (!el) continue;
        try { el.pause(); } catch { /* fine */ }
        // Drop the decoders + their buffers at once (a 137 MB film): clear the source, then detach.
        try { el.removeAttribute('src'); el.load(); } catch { /* fine */ }
      }
      overlay.remove();
      if (this.overlay === overlay) { this.overlay = null; this.video = null; this.img = null; this.narration = null; this.music = null; this.hint = null; this.stage = null; }
    }, this.fadeMs);
  }

  dispose(): void {
    if (this.overlay) setGameAudioMuted(false);
    for (const off of this.offs.splice(0)) off();
    if (this.poll !== null) { window.clearInterval(this.poll); this.poll = null; }
    if (this.fadeTimer !== null) { window.clearTimeout(this.fadeTimer); this.fadeTimer = null; }
    for (const t of this.rampTimers.splice(0)) window.clearTimeout(t);
    for (const el of [this.video, this.narration, this.music]) { try { el?.pause(); } catch { /* fine */ } }
    if (this.overlay) { this.overlay.remove(); this.overlay = null; this.video = null; this.img = null; this.narration = null; this.music = null; this.hint = null; this.stage = null; }
  }
}
